/* eslint-disable react-hooks/exhaustive-deps */
// @vendors
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';

// @services
import servicioService from '../../services/servicioService';

// @utils
import { COMPANY_STATUS_VALUES, PAYMENT_STATUS } from '../../utils/ConstantsUtils';

// @components
import EditChargeToPay from './components/EditChargeToPay';
import ChargeByCompany from './components/ChargeByCompany';

const Leads = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    totalLeads: 0,
    totalPayments: 0,
    nextMonth: 0,
    amountDue: 0
  });

  const [empresasAgrupadas, setEmpresasAgrupadas] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [companySelected, setCompanySelected] = useState(null);
  const [serviciosEmpresa, setServiciosEmpresa] = useState([]);
  const [loadingModal, setLoadingModal] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [newAmountDue, setNewAmountDue] = useState('');
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [chargeModalOpen, setChargeModalOpen] = useState(false);

  useEffect(() => {
    fetchMonthlyStats();
  }, [selectedYear, selectedMonth]);

  // function to fetch monthly stats
  const fetchMonthlyStats = async () => {
    try {
      setLoading(true);
      const data = await servicioService.getMonthlyCompanyStats(selectedYear, selectedMonth);
      const empresasArray = Array.isArray(data) ? data : [];
      successFetchMonthlyStats(empresasArray);
      toast.success('Estadísticas mensuales obtenidas');
      setError(null);
    } catch (err) {
      setError('No se pudieron cargar las estadísticas. Por favor, intenta de nuevo más tarde.');
      toast.error('Error al cargar estadísticas');
    } finally {
      setLoading(false);
    }
  };

  // function to success fetch monthly stats
  const successFetchMonthlyStats = (companyList) => {
    const companiesMapped = mapCompaniesData(companyList);
    setEmpresasAgrupadas(companiesMapped);
    const totalLeads = companiesMapped.reduce((sum, company) => sum + company?.totalServicios, 0);
    const totalPayments = companiesMapped.reduce((sum, company) => sum + company?.costoLeads, 0);
    const nextMonth = companiesMapped.filter(company => company?.serviceStatus === COMPANY_STATUS_VALUES.pending).reduce((sum, company) => sum + company?.amountDue, 0);
    const amountDue = companiesMapped.reduce((sum, company) => sum + company?.amountDue, 0);

    setStats({
      totalLeads,
      totalPayments,
      nextMonth,
      amountDue
    });
  }

  // function to map companies data
  const mapCompaniesData = (companyList) => {
    const companiesMap = companyList?.map(company => ({
      id: company.id,
      companyId: company.companyId || company.id,
      nombre: company.companyName || company.nombre || 'Sin empresa',
      totalServicios: company.serviceCount || 0,
      costoLeads: parseFloat(company.totalLeadsCost || 0),
      companyEmail: company.companyEmail,
      companyPhone: company.companyPhone,
      serviceStatus: company.serviceStatus,
      amountDue: company.amountDue,
      createdAt: company.createdAt,
      paymentDate: company.paymentDate,
    }));
    return companiesMap;
  }

  // Función para formatear la fecha
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';

    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      return 'Fecha inválida';
    }
  };

  // Función para formatear moneda
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Función optimizada para abrir el modal con los servicios de una empresa
  const handleEmpresaClick = async (empresa) => {
    try {
      setCompanySelected(empresa);
      setModalOpen(true);
      setLoadingModal(true);

      const serviciosDetallados = await servicioService.getCompanyMonthlyServices(
        empresa.companyId,
        selectedYear,
        selectedMonth
      );
      setServiciosEmpresa(Array.isArray(serviciosDetallados.services) ? serviciosDetallados.services : []);
    } catch (err) {
      toast.error('Error al cargar los servicios de la empresa');
      setServiciosEmpresa([]);
    } finally {
      setLoadingModal(false);
    }
  };

  // Función para cerrar el modal
  const handleCloseModal = () => {
    setModalOpen(false);
    setCompanySelected(null);
    setServiciosEmpresa([]);
  };

  const getPaymentStatus = (status) => {
    return PAYMENT_STATUS[status] || status;
  };

  const getStatusBadge = (status) => {
    const baseClasses = "flex items-center justify-center px-2 py-1 rounded-full leading-5 min-w-[85px]";
    if (status === 'Aprobada') return `${baseClasses} bg-green-200 `;
    if (status === 'pendiente') return `${baseClasses} bg-yellow-200`;
    if (status === 'pendingPayment') return `${baseClasses} bg-yellow-200`;
    if (status === 'Rechazada') return `${baseClasses} bg-red-200`;
    return `${baseClasses} bg-gray-200`;
  }

  const handleCharge = (empresa, event) => {
    event.stopPropagation();
    setCompanySelected(empresa);
    setChargeModalOpen(true);
  }

  const handleCloseChargeModal = () => {
    setChargeModalOpen(false);
    setCompanySelected(null);
  }

  const handleChargeSuccess = () => {
    fetchMonthlyStats();
    toast.success('Cobro procesado exitosamente');
  }

  const handleEdit = (empresa, event) => {
    event.stopPropagation();
    setCompanySelected(empresa);
    setNewAmountDue(empresa.amountDue.toString());
    setEditModalOpen(true);
  }

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setCompanySelected(null);
    setNewAmountDue('');
  }

  // Función para guardar los cambios del costo a pagar
  const handleSaveAmountDue = async () => {
    if (!companySelected || !newAmountDue || isNaN(parseFloat(newAmountDue))) return toast.error('Por favor ingresa un valor válido');
    try {
      setLoadingEdit(true);
      const data = {
        companyId: companySelected.companyId,
        totalLeadsCost: companySelected.costoLeads,
        amountDue: parseInt(newAmountDue),
        transactionStatus: companySelected.serviceStatus,
        paymentDate: null,
        serviceCount: companySelected.totalServicios
      }
      await servicioService.updateAmountDue(companySelected.id, data);
      toast.success('Costo a pagar actualizado correctamente');
      fetchMonthlyStats();
      handleCloseEditModal();
    } catch (error) {
      toast.error('Error al actualizar el costo a pagar, por favor intenta de nuevo más tarde');
    } finally {
      setLoadingEdit(false);
    }
  }

  const totalCostoServices = (services) => {
    return services.reduce((sum, service) => sum + parseFloat(service?.leadPrice) || 0, 0);
  }

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-6">Dashboard de Leads</h2>

      {/* Filtros de año y mes */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <h3 className="text-lg font-medium text-gray-700 mb-4">Filtros</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Año
            </label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Mes
            </label>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                <option key={month} value={month}>
                  {new Date(2024, month - 1).toLocaleDateString('es-ES', { month: 'long' })}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Cards de estadísticas */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {/* Card 1: Total de Leads */}
        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-blue-500">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-100 mr-4">
              <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
              </svg>
            </div>
            <div>
              <p className="text-gray-500 text-sm">Total Leads Conseguidos</p>
              <p className="text-2xl font-bold">{stats.totalLeads}</p>
            </div>
          </div>
        </div>

        {/* Card 2: Precio Total */}
        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-green-500">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-100 mr-4">
              <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
            <div>
              <p className="text-gray-500 text-sm">Precio Total de Leads</p>
              <p className="text-2xl font-bold">{formatCurrency(stats.totalPayments)}</p>
            </div>
          </div>
        </div>

        {/* Card 3: Próximo Mes */}
        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-purple-500">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-purple-100 mr-4">
              <svg className="w-8 h-8 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
              </svg>
            </div>
            <div>
              <p className="text-gray-500 text-sm">Costo a recibir Próximo Mes</p>
              <p className="text-2xl font-bold">{formatCurrency(stats.nextMonth)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabla de empresas */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-xl font-bold mb-4">Empresas y sus Leads</h3>

        {/* Estado de carga y error */}
        {loading && <p className="mt-4 text-gray-600">Cargando datos...</p>}
        {error && <p className="mt-4 text-red-500">{error}</p>}

        {/* Tabla */}
        {!loading && !error && (
          <div className="overflow-x-auto mt-6">
            <table className="min-w-full bg-white">
              <thead>
                <tr className="w-full h-16 border-gray-300 border-b py-8">
                  <th className="text-left pl-6 font-bold text-gray-600">Empresa</th>
                  <th className="text-center font-bold text-gray-600">Correo</th>
                  <th className="text-center font-bold text-gray-600">Teléfono</th>
                  <th className="text-center font-bold text-gray-600">Estado</th>
                  <th className="text-center font-bold text-gray-600">Número de Servicios</th>
                  <th className="text-right pr-6 font-bold text-gray-600">Costo Leads</th>
                  <th className="text-right pr-6 font-bold text-gray-600">Costo a pagar</th>
                  <th className="text-center pr-6 font-bold text-gray-600">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {empresasAgrupadas.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center py-6 text-gray-500">
                      No hay empresas registradas.
                    </td>
                  </tr>
                ) : (
                  empresasAgrupadas.map(empresa => (
                    <tr
                      key={empresa.id}
                      className="h-16 border-gray-300 border-b hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => handleEmpresaClick(empresa)}
                      title="Haz clic para ver los servicios de esta empresa"
                    >
                      <td className="py-3 px-6 text-left font-medium">{empresa.nombre}</td>
                      <td className="py-3 px-6 text-center">{empresa.companyEmail}</td>
                      <td className="py-3 px-6 text-center">{empresa.companyPhone}</td>
                      <td className="py-3 px-6 text-center">
                        <div className={getStatusBadge(empresa.serviceStatus)}>
                          {getPaymentStatus(empresa.serviceStatus)}
                        </div>
                      </td>
                      <td className="py-3 px-6 text-center">{empresa.totalServicios}</td>
                      <td className="py-3 px-6 text-right">{formatCurrency(empresa.costoLeads)}</td>
                      <td className="py-3 px-6 text-right">{formatCurrency(empresa.amountDue)}</td>
                      <td className="py-3 px-6 text-center">
                        <div className="flex justify-center gap-2">
                          <button
                            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={empresa.serviceStatus === 'Aprobada'}
                            onClick={(event) => handleEdit(empresa, event)}
                          >
                            Editar
                          </button>

                          <button
                            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={empresa.serviceStatus === 'Aprobada'}
                            onClick={(event) => handleCharge(empresa, event)}
                          >
                            Cobrar
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              <tfoot>
                <tr className="h-16 bg-gray-50">
                  <td colSpan="1" className="py-3 px-6 text-left font-bold">Totales</td>
                  <td className="py-3 px-6 text-center font-bold"></td>
                  <td className="py-3 px-6 text-center font-bold"></td>
                  <td className="py-3 px-6 text-center font-bold"></td>
                  <td className="py-3 px-6 text-center font-bold">{stats.totalLeads}</td>
                  <td className="py-3 px-6 text-right font-bold">{formatCurrency(stats.totalPayments)}</td>
                  <td className="py-3 px-6 text-right font-bold">{formatCurrency(stats.amountDue)}</td>
                  <td className="py-3 px-6 text-center font-bold"></td>
                  <td className="py-3 px-6 text-center font-bold"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* Modal para mostrar servicios de la empresa */}
      {modalOpen && companySelected && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold">
                Servicios de {companySelected.nombre}
              </h3>
              <button
                onClick={handleCloseModal}
                className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
              >
                ×
              </button>
            </div>

            <div className="mb-4">
              <p className="text-gray-600">
                <strong>Total de servicios:</strong> {serviciosEmpresa?.length ?? 0} |
                <strong> Costo total de leads:</strong> {formatCurrency(totalCostoServices(serviciosEmpresa))}
              </p>
            </div>

            {/* Estado de carga del modal */}
            {loadingModal ? (
              <div className="flex justify-center items-center py-8">
                <div className="text-gray-600">Cargando servicios de la empresa...</div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white border border-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Usuario
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Servicio
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Fecha
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Categoría
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Precio Lead
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {serviciosEmpresa.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                          No hay servicios para esta empresa en el período seleccionado.
                        </td>
                      </tr>
                    ) : (
                      serviciosEmpresa.map((servicio, index) => (
                        <tr key={`${servicio.id}-${servicio.userId}-${index}`} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {servicio.userName || 'Usuario desconocido'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {servicio.serviceTitle || servicio.serviceName || 'Sin nombre'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatDate(servicio.serviceDate || servicio.createdAt)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {servicio.categoryName || 'Sin categoría'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                            {formatCurrency(parseFloat(servicio.leadPrice) || 0)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                  {serviciosEmpresa.length > 0 && (
                    <tfoot className="bg-gray-50">
                      <tr>
                        <td colSpan="4" className="px-6 py-3 text-left text-sm font-medium text-gray-900">
                          Total
                        </td>
                        <td className="px-6 py-3 text-right text-sm font-medium text-gray-900">
                          {formatCurrency(totalCostoServices(serviciosEmpresa))}
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            )}

            <div className="mt-6 flex justify-end">
              <button
                onClick={handleCloseModal}
                className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para editar el costo a pagar */}
      {editModalOpen && companySelected && (
        <EditChargeToPay
          companySelected={companySelected}
          newAmountDue={newAmountDue}
          setNewAmountDue={setNewAmountDue}
          handleCloseEditModal={handleCloseEditModal}
          loadingEdit={loadingEdit}
          handleSaveAmountDue={handleSaveAmountDue}
          formatCurrency={formatCurrency}
        />
      )}

      {/* Modal para procesar cobro */}
      {chargeModalOpen && companySelected && (
        <ChargeByCompany
          isOpen={chargeModalOpen}
          onClose={handleCloseChargeModal}
          companySelected={companySelected}
          onChargeSuccess={handleChargeSuccess}
          formatCurrency={formatCurrency}
        />
      )}
    </div>
  );
};

export default Leads;
